/**
 * PHARMAKON — Trésorerie · M4, the caissier's « Ma caisse »: the card of `/caissier` and of « Mouvements
 * de ma caisse » (`/ma-tresorerie`), the « Transférer depuis ma caisse » dialog, the withdrawal of a
 * demand, the receiving toast and the bon de transfert (TWP-22).
 *
 * Verbatim French UI copy of design/ux/02_comptes_caisses_mouvements.md §13 (the contract for E2E
 * locators). Sentence case; « DA » comes from the money formatter, never typed here; no amount is
 * computed here (03_UX_DESIGN P-5) — a sentence that names an amount receives it already formatted.
 * Every business word the server owns (destinations, their « immédiat / à valider » mention, motifs,
 * the preview's refusals, résumé and effect, the reason of a card without session, the demand states)
 * is served by `GET /comptoirs/moi` and the preview and shown verbatim; only the screen's own words
 * live here.
 */

/** §13.1 — the « Ma caisse » card (three actions, the drawer's figure, the last movements, the demands). */
export const CARTE = Object.freeze({
  titre: (comptoir) => (comptoir ? `Ma caisse — ${comptoir}` : "Ma caisse"),
  regionLabel: "Ma caisse : encaisser, décaisser, transférer",
  // Trésorerie TWP-55 (TO-10) — the manager's own session from his dashboard.
  cloturerMaSession: "Clôturer ma session",
  ouvrirMaSession: "Ouvrir ma session",
  sessionDepuis: (heure) => `Session ouverte depuis ${heure}`,
  sessionDepuisLe: (date, heure) => `Session ouverte depuis le ${date} à ${heure}`,
  attendues: "Espèces attendues",
  afficher: "Afficher",
  masquer: "Masquer",
  afficherAide: "Afficher le montant",
  masquerAide: "Masquer le montant",
  aveugle: "Comptage à l'aveugle : révélé à la clôture",
  // Trésorerie TWP-60 (TO-15, TD-348) — the officine hides the cashier's cash: never revealed to him.
  masqueResponsable: "Masqué par le responsable",
  chiffreCache: "••••••",
  encaisser: "Encaisser",
  decaisser: "Décaisser",
  transferer: "Transférer",
  sansSessionDefaut: "Ouvrez votre caisse pour encaisser, décaisser ou transférer.",
  chargement: "Lecture de votre caisse…",
  erreur: "Votre caisse n'a pas pu être lue.",
  derniers: "Derniers mouvements",
  aucunMouvement: "Aucun mouvement de trésorerie sur cette session.",
  voirTout: "Mouvements de ma caisse",
  demandes: "Vos demandes en attente",
  aRecevoir: "Transferts à recevoir",
  retirer: "Retirer…",
  accuser: "Accuser réception",
  accuseFait: (montant, depuis) => `${montant} reçus de ${depuis} : votre caisse est à jour.`,
  ligneDemande: (montant, vers, etat) => `${montant} vers ${vers} — ${etat}`,
  ligneArrivee: (montant, depuis, qui) => `${montant} depuis ${depuis}${qui ? ` (${qui})` : ""}`,
  prelevement: "Prélèvement propriétaire",
  horsTresorerie: "Hors trésorerie",
  imprimerBon: "Bon de transfert",
  imprimerBonAide: "Imprimer le bon de transfert",
});

/** §13.3 — « Transférer depuis ma caisse » (Modal md 640). */
export const TRANSFERT = Object.freeze({
  titre: "Transférer depuis ma caisse",
  depuis: (comptoir) => `Depuis : ${comptoir} — votre caisse`,
  definiParSession: "Défini par votre session",
  attendues: "Espèces attendues",
  aveugle: "Comptage à l'aveugle : votre caisse se révèle à la clôture.",
  // Trésorerie TWP-60 (TO-15, TD-348) — the officine hides the cashier's cash: never revealed to him.
  masqueResponsable: "Votre caisse est masquée par le responsable.",
  vers: "Vers",
  versAide: "Chiffres 1 à 9 pour choisir, flèches pour parcourir.",
  aucuneDestination: "Aucune destination n'est ouverte à votre caisse : demandez au propriétaire.",
  ouvertPar: (caissier) => (caissier ? `ouvert (${caissier})` : "ouvert"),
  montant: "Montant",
  motif: "Motif",
  motifAide: "Présélectionné selon la destination ; vous pouvez le changer.",
  motifAucun: "Aucun motif disponible pour cette destination : le propriétaire les règle dans Paramètres de trésorerie.",
  precision: "Précision",
  precisionFacultative: "Précision (facultatif)",
  precisionRequise: (min) => `Obligatoire avec ce motif : ${min} caractères au moins.`,
  bon: "Imprimer un bon de transfert",
  bonAide: "Un reçu à faire signer par les deux comptoirs (facultatif).",
  annuler: "Annuler",
  transferer: (montant) => (montant ? `Transférer ${montant}` : "Transférer"),
  demander: (montant) => (montant ? `Envoyer la demande de ${montant}` : "Envoyer la demande"),
  enCours: "Enregistrement…",
  chargement: "Lecture de votre caisse…",
  calcul: "calcul…",
  confirmTitre: "Confirmer le transfert",
  confirmTitreDemande: "Confirmer la demande",
  confirmer: (montant) => `Transférer ${montant}`,
  confirmerDemande: "Envoyer la demande",
  succes: (montant, vers) => `Transfert de ${montant} vers ${vers} enregistré.`,
  succesDemande: (montant, vers) => `Demande de ${montant} vers ${vers} enregistrée : le propriétaire doit la valider.`,
  succesTransit: (montant, vers) => `${montant} en transit vers ${vers} : le comptoir destinataire doit accuser réception.`,
  rejoue: "Ce transfert était déjà enregistré : rien n'a été ajouté.",
  ouvrir: "Voir",
  bonImpossible: "Le transfert est enregistré, mais le bon n'a pas pu être imprimé. Réimprimez-le depuis « Mouvements de ma caisse ».",
});

/** The author withdraws his own demand still waiting (TD-284 « the caissier is never stuck »). */
export const RETRAIT = Object.freeze({
  titre: "Retirer la demande",
  question: (ligne) => `Retirer la demande ${ligne} ? Votre caisse ne change pas : rien n'avait encore bougé.`,
  motif: "Motif du retrait",
  motifAide: (min) => `${min} caractères au moins (ex. erreur de destination).`,
  confirmer: "Retirer la demande",
  annuler: "Garder la demande",
  succes: "Demande retirée.",
});

/** §13.4 — the receiving side (SSE toast, Info 5 s, role="status", never takes focus). */
export const RECEPTION = Object.freeze({
  recu: (montant, depuis, qui, motif, note) =>
    `${montant} reçus de ${depuis}${qui ? ` (${qui})` : ""}${motif ? ` — ${motif}${note ? ` : ${note}` : ""}` : ""}`,
  transit: (montant, depuis) => `${montant} en transit depuis ${depuis}`,
  voir: "Voir",
  accuser: "Accuser réception",
});

/** §13.5 — « Mouvements de ma caisse » (`/ma-tresorerie`). */
export const MA_CAISSE = Object.freeze({
  titre: "Mouvements de ma caisse",
  sousTitre: "Chaque mouvement de votre tiroir, transferts compris, session par session.",
  session: "Session",
  sessionOption: (date, comptoir, ouverte) => `${date} — ${comptoir}${ouverte ? " (ouverte)" : ""}`,
  aucuneSession: "Vous n'avez encore ouvert aucune session sur une caisse comptoir.",
  grille: "Mouvements de ma caisse",
  colonnes: Object.freeze({
    heure: "Heure",
    numero: "N°",
    type: "Type",
    motif: "Motif",
    flux: "Depuis ⟶ Vers",
    montant: "Montant (DA)",
    statut: "Statut",
    saisiPar: "Saisi par",
  }),
  enAttente: "en attente",
  masque: "Masqué",
  nonNumerote: "—",
  totaux: Object.freeze({
    label: "Totaux de la session",
    entrees: "Entrées",
    sorties: "Sorties",
    solde: "Solde de la période",
    masques: "Certains montants sont masqués pour votre rôle.",
    note: "Calculés par le serveur sur toute la session (toutes les pages).",
  }),
  vide: "Aucun mouvement de trésorerie sur cette session.",
  plus: "Plus anciens",
  nb: (n) => (n === 1 ? "1 mouvement" : `${n} mouvements`),
  misAJour: (heure) => `Mis à jour à ${heure}`,
});
